import axios from "axios";
import queryString from "query-string";

export type AffiliateLinkContext =
  | "bonus_card"
  | "casino_card"
  | "guide_content"
  | "casino_review"
  | "casino_redirect"
  | "general_link_redirect";

export interface ITrackingMetadata {
  casino: { slug: string; name: string };
  placement: string;
  linkContext: AffiliateLinkContext;
  destinationId?: string;
}

interface IUseTracking {
  track: (metadata: ITrackingMetadata) => Promise<void>;
  storeClickId: () => void;
}

type AdSource = "Adavice";

type ClickIdParam = { param: string; source: AdSource };

const clickIds = [{ param: "adaid", source: "Adavice" }] as ClickIdParam[];

const storageItemName = "clickId";
const trackingTimeoutMs = 1000;

type ClickId = { clickId: string; source: AdSource };

const useTracking = (): IUseTracking => {
  const storeClickId = () => {
    const params = queryString.parse(location.search);
    const matches: ClickId[] = [];

    // Look for a matching click ids
    clickIds.forEach((value) => {
      const clickId = params[value.param];
      if (clickId) {
        matches.push({
          clickId: Array.isArray(clickId) ? clickId[0]! : clickId,
          source: value.source,
        });
      }
    });

    if (matches.length > 0)
      setStorageItem(storageItemName, JSON.stringify(matches[0]));
  };

  const getClickId = () => {
    const item = getStorageItem(storageItemName);

    if (!item) return undefined;

    try {
      return JSON.parse(item) as ClickId;
    } catch {
      // Old or corrupt attribution storage must not interrupt an outbound visit.
      return undefined;
    }
  };

  const trackReddit = async (metadata: ITrackingMetadata) => {
    // Can only use window if we are in a browser, not in a build
    if (typeof rdt === "function") {
      rdt("track", "AddToCart", {
        products: [
          {
            id: metadata.casino.slug,
            name: metadata.casino.name,
          },
        ],
      });
    }
  };

  const trackS2S = async () => {
    const clickId = getClickId();
    if (clickId) {
      await axios.post("/api/track", clickId, {
        timeout: trackingTimeoutMs,
      });
    }
  };

  const trackGA = (metadata: ITrackingMetadata): Promise<void> => {
    const clickId = getClickId();

    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let finished = false;

      const finish = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(fallbackTimeout);
        resolve();
      };

      const fallbackTimeout = window.setTimeout(finish, trackingTimeoutMs);

      window.gtag?.("event", "casino_visit", {
        // Keep the existing GA4 "Casino" custom dimension populated during migration.
        name: metadata.casino.name,
        casino_id: metadata.casino.slug,
        casino_name: metadata.casino.name,
        page_path: window.location.pathname,
        placement: metadata.placement,
        link_context: metadata.linkContext,
        destination_id: metadata.destinationId ?? metadata.casino.slug,
        traffic_source: clickId?.source,
        transport_type: "beacon",
        event_callback: finish,
        event_timeout: trackingTimeoutMs,
      });
    });
  };

  const track = async (metadata: ITrackingMetadata) => {
    const reddit = trackReddit(metadata);
    const s2s = trackS2S();
    const ga = trackGA(metadata);

    // A blocked advertising endpoint must not prevent navigation.
    await Promise.allSettled([reddit, s2s, ga]);
  };

  return { track, storeClickId };
};

export default useTracking;

const setStorageItem = (key: string, value: string) => {
  if (typeof window !== "undefined") {
    localStorage.setItem(key, value);
  }
};

const getStorageItem = (key: string) => {
  if (typeof window !== "undefined") {
    return localStorage.getItem(key);
  }

  return null;
};
